import React from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Logo from 'components/Logo';
import useSEO from 'hooks/useSEO';

const EFFECTIVE_DATE = 'March 1, 2025';
const COMPANY       = 'Safe Dividend Tracker';
const DOMAIN        = 'safedividendtracker.com';
const EMAIL_SUPPORT = 'support@safedividendtracker.com';

function SectionTitle({ children }) {
  return <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1E293B', fontFamily: 'var(--font-heading)', marginBottom: 12, marginTop: 36 }}>{children}</h2>;
}
function Para({ children }) {
  return <p style={{ fontSize: 15, color: '#475569', lineHeight: 1.8, marginBottom: 14 }}>{children}</p>;
}
function BulletList({ items }) {
  return (
    <ul style={{ paddingLeft: 20, marginBottom: 14 }}>
      {items.map((item, i) => <li key={i} style={{ fontSize: 15, color: '#475569', lineHeight: 1.75, marginBottom: 6, listStyleType: 'disc' }}>{item}</li>)}
    </ul>
  );
}

export default function TermsPage() {
  useSEO({
    title: 'Terms of Service',
    description: 'Read the Safe Dividend Tracker terms of service. Understand the rules and conditions for using our free dividend portfolio tracking platform.',
    canonical: '/terms',
  });

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC' }}>
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: '0 48px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <Logo size={36} />
          <span style={{ fontSize: 17, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', fontFamily: 'var(--font-heading)' }}>Safe Dividend Tracker</span>
        </Link>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          {[['/#features', 'Features'], ['/#pricing', 'Pricing'], ['/blog', 'Blog'], ['/#faq', 'FAQ'], ['/contact', 'Contact'], ['/privacy-policy', 'Privacy']].map(([href, label]) => (
            <Link key={label} to={href} style={{ fontSize: 14, color: '#64748B', fontWeight: 500, fontFamily: 'var(--font-caption)', textDecoration: 'none' }}>{label}</Link>
          ))}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/login" style={{ fontSize: 14, color: '#475569', fontWeight: 500, textDecoration: 'none', padding: '8px 16px', border: '1px solid #E2E8F0', borderRadius: 8, fontFamily: 'var(--font-caption)' }}>Sign In</Link>
          <Link to="/register" style={{ fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#2E7D32,#388E3C)', padding: '8px 18px', borderRadius: 8, textDecoration: 'none', fontFamily: 'var(--font-caption)', boxShadow: '0 2px 8px rgba(46,125,50,0.3)' }}>Get Started Free</Link>
        </div>
      </header>

      <div style={{ background: 'linear-gradient(180deg, #EEF2FF 0%, #F8FAFC 100%)', borderBottom: '1px solid #E2E8F0' }}>
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" style={{ fontSize: 13, color: '#64748B' }}>Home</Link>
            <Icon name="ChevronRight" size={14} color="#94A3B8" />
            <span style={{ fontSize: 13, color: '#1E293B' }}>Terms of Service</span>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 mt-1" style={{ background: 'rgba(46,125,50,0.1)', border: '1px solid rgba(46,125,50,0.2)' }}>
              <Icon name="FileText" size={28} color="#2E7D32" />
            </div>
            <div>
              <h1 style={{ fontSize: 34, fontWeight: 800, color: '#0F172A', fontFamily: 'var(--font-heading)' }}>Terms of Service</h1>
              <p style={{ fontSize: 14, color: '#64748B', marginTop: 6, fontFamily: 'var(--font-caption)' }}>Effective date: <strong>{EFFECTIVE_DATE}</strong></p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <Para>
          Please read these Terms of Service ("<strong>Terms</strong>") carefully before using {COMPANY} (the "<strong>Service</strong>") operated by {COMPANY} ("<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>"). By accessing or using the Service, you agree to be bound by these Terms.
        </Para>

        <SectionTitle>1. Acceptance of Terms</SectionTitle>
        <Para>By creating an account or using the Service, you confirm that you are at least 18 years old, have read and understood these Terms, and agree to be bound by them. If you do not agree, you must not use the Service.</Para>

        <SectionTitle>2. Description of Service</SectionTitle>
        <Para>{COMPANY} is a web application that helps investors track dividend income, manage investment portfolios, view dividend calendars, project income growth via DRIP reinvestment, and connect brokerage accounts for automatic portfolio synchronisation.</Para>
        <Para><strong>Not financial advice:</strong> The Service provides tools and information for personal tracking and educational purposes only. Nothing in the Service constitutes financial, investment, tax, or legal advice. Always consult a qualified financial adviser before making investment decisions.</Para>

        <SectionTitle>3. User Accounts</SectionTitle>
        <BulletList items={[
          'You must provide accurate registration information and keep it updated.',
          'You are responsible for maintaining the security of your account credentials.',
          'You must not share your account with others or allow unauthorised access.',
          'You must notify us immediately at ' + EMAIL_SUPPORT + ' if you suspect unauthorised use of your account.',
          'We reserve the right to suspend or terminate accounts that violate these Terms.',
        ]} />

        <SectionTitle>4. Subscription Plans &amp; Billing</SectionTitle>
        <Para>The Service is offered on a freemium basis with paid subscription tiers (Premium and Pro). By subscribing to a paid plan:</Para>
        <BulletList items={[
          'You authorise us to charge your payment method on a recurring basis.',
          'Subscriptions renew automatically unless cancelled at least 24 hours before the renewal date.',
          'All payments are processed by Stripe and are subject to Stripe\'s terms of service.',
          'Refunds are issued at our discretion, generally within 7 days of the charge if a billing error occurred.',
          'Downgrading your plan takes effect at the end of your current billing period.',
          'We reserve the right to change pricing with 30 days\' notice.',
        ]} />

        <SectionTitle>5. Acceptable Use</SectionTitle>
        <Para>You agree not to:</Para>
        <BulletList items={[
          'Use the Service for any unlawful purpose or in violation of any applicable laws.',
          'Attempt to gain unauthorised access to any part of the Service or its servers.',
          'Scrape, crawl, or systematically harvest data from the Service.',
          'Upload, post, or transmit any harmful, offensive, or disruptive content.',
          'Reverse engineer, decompile, or disassemble any part of the Service.',
          'Use automated scripts or bots to interact with the Service.',
          'Interfere with or disrupt the integrity or performance of the Service.',
        ]} />

        <SectionTitle>6. Data Accuracy &amp; Disclaimer</SectionTitle>
        <Para>Dividend data, stock prices, and market information displayed in the Service are sourced from third parties (including Financial Modeling Prep) and may not always be accurate, complete, or up to date. We make no warranties regarding the accuracy of this data. You should independently verify any financial information before relying on it.</Para>
        <Para>DRIP projections and income forecasts are hypothetical illustrations based on current data and constant-rate assumptions. They do not represent guaranteed future results.</Para>

        <SectionTitle>7. Intellectual Property</SectionTitle>
        <Para>The Service, including its design, code, text, graphics, and all other content created by us, is owned by {COMPANY} and protected by copyright and other intellectual property laws. You are granted a limited, non-exclusive, non-transferable licence to use the Service for your personal, non-commercial purposes.</Para>
        <Para>Your portfolio data, holding information, and personal data remain yours. We claim no ownership over data you enter into the Service.</Para>

        <SectionTitle>8. Brokerage Integrations</SectionTitle>
        <Para>Brokerage connections are powered by SnapTrade, a third-party service. By connecting a brokerage account, you also agree to SnapTrade's terms of service. We are not responsible for the accuracy of brokerage-synced data or for any actions taken by SnapTrade. We receive only read-only access to your portfolio positions — we cannot place trades or withdraw funds.</Para>

        <SectionTitle>9. Limitation of Liability</SectionTitle>
        <Para>To the fullest extent permitted by law, {COMPANY} shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or goodwill, arising from your use of or inability to use the Service, even if we have been advised of the possibility of such damages.</Para>
        <Para>Our total liability for any claim arising from your use of the Service shall not exceed the amount you paid us in the 12 months preceding the claim.</Para>

        <SectionTitle>10. Indemnification</SectionTitle>
        <Para>You agree to indemnify and hold harmless {COMPANY} and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or related to your use of the Service or violation of these Terms.</Para>

        <SectionTitle>11. Termination</SectionTitle>
        <Para>You may terminate your account at any time from the Settings page or by emailing us. We may suspend or terminate your account immediately if you breach these Terms or if we are required to do so by law.</Para>
        <Para>Upon termination, your right to access the Service ceases. We will retain your data for 60 days per our Privacy Policy, after which it will be permanently deleted.</Para>

        <SectionTitle>12. Governing Law</SectionTitle>
        <Para>These Terms are governed by and construed in accordance with the laws applicable in the jurisdiction where {COMPANY} is registered, without regard to conflict of law principles. Any disputes shall be resolved in the courts of that jurisdiction.</Para>

        <SectionTitle>13. Changes to Terms</SectionTitle>
        <Para>We may modify these Terms at any time. Material changes will be notified via email at least 14 days before they take effect. Continued use of the Service after the effective date constitutes acceptance of the new Terms.</Para>

        <SectionTitle>14. Contact</SectionTitle>
        <Para>Questions about these Terms? Contact us at <a href={`mailto:${EMAIL_SUPPORT}`} style={{ color: '#2E7D32' }}>{EMAIL_SUPPORT}</a>.</Para>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-14 pt-8" style={{ borderTop: '1px solid #E2E8F0' }}>
          <Link to="/" style={{ fontSize: 14, color: '#2E7D32', fontFamily: 'var(--font-caption)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="ArrowLeft" size={15} color="#2E7D32" /> Back to Home
          </Link>
          <Link to="/privacy-policy" style={{ fontSize: 14, color: '#64748B', fontFamily: 'var(--font-caption)' }}>Privacy Policy →</Link>
        </div>
      </div>

      <footer style={{ background: '#F1F5F9', borderTop: '1px solid #E2E8F0' }} className="px-6 py-8">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p style={{ fontSize: 13, color: '#94A3B8', fontFamily: 'var(--font-caption)' }}>© {new Date().getFullYear()} {COMPANY}. All rights reserved.</p>
          <div className="flex gap-6">
            {[['Privacy Policy','/privacy-policy'],['Terms of Service','/terms'],['Home','/']].map(([l,h]) => (
              <Link key={l} to={h} style={{ fontSize: 13, color: '#64748B', fontFamily: 'var(--font-caption)' }}>{l}</Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
